import styles from "./WhiteLabelBreadCrumbs.module.scss";
import CustomLink from "@Components/CustomLink";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import { useLocation } from "react-router-dom";

interface Props {
  pageType: "search" | "property" | "custom" | "country";
  regionName?: string;
  regionSlug?: string;
  propertyName?: string;
  customPageTitle?: string;
  className?: string;
  searchQueryParams?: string;
}

const WhiteLabelBreadCrumbs = ({
  pageType,
  regionName,
  regionSlug,
  propertyName,
  customPageTitle,
  className,
  searchQueryParams = "",
}: Props) => {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const { isDemoSubdomain, partner_uuid } = useWhitelabel();

  const buildHomeUrl = () => convertToWhitelabelURL("/", isDemoSubdomain, partner_uuid);

  const buildRegionSearchUrl = () => {
    if (!regionSlug) return buildHomeUrl();

    const baseUrl = `/places/search/${regionSlug}`;

    // If property page passed filters explicitly → use them
    if (searchQueryParams?.length > 0) {
      return convertToWhitelabelURL(
        `${baseUrl}${searchQueryParams}`,
        isDemoSubdomain,
        partner_uuid,
      );
    }

    // Otherwise just use current URL search (if any)
    if (location.search) {
      return convertToWhitelabelURL(`${baseUrl}${location.search}`, isDemoSubdomain, partner_uuid);
    }

    // Default fallback → no filters
    return convertToWhitelabelURL(baseUrl, isDemoSubdomain, partner_uuid);
  };

  const renderItem = (label: string, link?: string, isLast?: boolean) => (
    <li className={isLast ? styles.truncate : ""}>
      {link && !isLast ? (
        <CustomLink to={link} className={styles.name}>
          {label}
        </CustomLink>
      ) : (
        <span className={styles.name}>{label}</span>
      )}
    </li>
  );

  const renderBreadcrumbs = () => {
    const items = [];

    items.push(renderItem("Home", buildHomeUrl()));

    if (pageType === "search" && regionName) {
      items.push(renderItem(regionName, buildRegionSearchUrl()));
    }

    if (pageType === "country" && regionName) {
      items.push(renderItem(regionName, undefined, true));
    }

    if (pageType === "property" && regionName && propertyName) {
      items.push(renderItem(regionName, buildRegionSearchUrl()));
      items.push(renderItem(propertyName, undefined, true));
    }

    if (pageType === "custom" && customPageTitle) {
      items.push(renderItem(customPageTitle, undefined, true));
    }

    return items;
  };

  return (
    <ul className={formatClassNames(styles.container, className, isDesktop && styles.desktop)}>
      {renderBreadcrumbs()}
    </ul>
  );
};

export default wrapperHOC(WhiteLabelBreadCrumbs, {
  componentName: "WhiteLabelBreadCrumbs",
  showForWhiteLable: true,
  showForChina: false,
  showForApp: false,
});
